import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  trackPageView,
  trackEvent,
  trackSignup,
  trackLogin,
  trackFeatureUse,
  trackFormSubmit,
  trackCTAClick,
  trackSearch,
  trackCampInteraction,
  trackEmailIntegration,
  trackSocialInteraction,
  analyticsConfig
} from '@/utils/analytics';

/**
 * Analytics Testing Component
 * 
 * Use this component to test that Google Analytics 4 is properly configured
 * and sending events. Only works in staging/production environments.
 * 
 * To use:
 * 1. Navigate to /analytics-test in staging
 * 2. Open Google Analytics > Configure > DebugView
 * 3. Click the test buttons below
 * 4. Verify events appear in DebugView
 */
const AnalyticsTest = () => {
  const [lastEvent, setLastEvent] = useState(null);

  const handleTestEvent = (eventName, trackFunction) => {
    trackFunction();
    setLastEvent(eventName);
  };

  return (
    <div className="container mx-auto p-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Google Analytics 4 Testing</h1>
        <p className="text-gray-600">
          Test the GA4 integration by clicking the buttons below. 
          Check Google Analytics DebugView to see events in real-time.
        </p>
      </div>

      {/* Configuration Status */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Configuration Status</CardTitle>
          <CardDescription>Current analytics configuration</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="font-medium">Analytics Enabled:</span>
              <span className={analyticsConfig.enabled ? 'text-green-600' : 'text-red-600'}>
                {analyticsConfig.enabled ? '✓ Yes' : '✗ No'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-medium">Environment:</span>
              <span>{analyticsConfig.environment}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-medium">Measurement ID:</span>
              <span className="font-mono text-sm">
                {analyticsConfig.measurementId || 'Not configured'}
              </span>
            </div>
          </div>

          {!analyticsConfig.enabled && (
            <Alert className="mt-4 border-yellow-200 bg-yellow-50">
              <AlertDescription className="text-yellow-800">
                <strong>Analytics Disabled:</strong> Analytics only work in staging and production environments. 
                Make sure VITE_GA_MEASUREMENT_ID and VITE_APP_ENV are properly set.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Last Event Triggered */}
      {lastEvent && (
        <Alert className="mb-6 border-green-200 bg-green-50">
          <AlertDescription className="text-green-800">
            <strong>Event Triggered:</strong> {lastEvent}
            <br />
            <span className="text-sm">
              Check your GA4 DebugView to see this event in real-time
            </span>
          </AlertDescription>
        </Alert>
      )}

      {/* Test Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Core Events */}
        <Card>
          <CardHeader>
            <CardTitle>Core Events</CardTitle>
            <CardDescription>Standard tracking events</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button 
              onClick={() => handleTestEvent('Page View', () => trackPageView('/test-page', 'Test Page'))}
              className="w-full"
              variant="outline"
            >
              Test Page View
            </Button>
            <Button 
              onClick={() => handleTestEvent('Custom Event', () => trackEvent('test_event', { test: true }))}
              className="w-full"
              variant="outline"
            >
              Test Custom Event
            </Button>
            <Button 
              onClick={() => handleTestEvent('Signup', () => trackSignup('email'))}
              className="w-full"
              variant="outline"
            >
              Test Signup Event
            </Button>
            <Button 
              onClick={() => handleTestEvent('Login', () => trackLogin('email'))}
              className="w-full"
              variant="outline"
            >
              Test Login Event
            </Button>
          </CardContent>
        </Card>

        {/* Feature Events */}
        <Card>
          <CardHeader>
            <CardTitle>Feature Events</CardTitle>
            <CardDescription>Feature usage tracking</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button 
              onClick={() => handleTestEvent('Feature Use', () => trackFeatureUse('test_feature', { test: true }))}
              className="w-full"
              variant="outline"
            >
              Test Feature Use
            </Button>
            <Button 
              onClick={() => handleTestEvent('Form Submit', () => trackFormSubmit('test_form', { success: true }))}
              className="w-full"
              variant="outline"
            >
              Test Form Submit
            </Button>
            <Button 
              onClick={() => handleTestEvent('CTA Click', () => trackCTAClick('test_button', { location: 'test_page' }))}
              className="w-full"
              variant="outline"
            >
              Test CTA Click
            </Button>
            <Button 
              onClick={() => handleTestEvent('Search', () => trackSearch('test query', { results: 10 }))}
              className="w-full"
              variant="outline"
            >
              Test Search Event
            </Button>
          </CardContent>
        </Card>

        {/* Camp Events */}
        <Card>
          <CardHeader>
            <CardTitle>Camp Events</CardTitle>
            <CardDescription>Camp-specific interactions</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button 
              onClick={() => handleTestEvent('Camp View', () => trackCampInteraction('view', 'test_camp_123', { name: 'Test Camp' }))}
              className="w-full"
              variant="outline"
            >
              Test Camp View
            </Button>
            <Button 
              onClick={() => handleTestEvent('Camp Bookmark', () => trackCampInteraction('bookmark', 'test_camp_123'))}
              className="w-full"
              variant="outline"
            >
              Test Camp Bookmark
            </Button>
            <Button 
              onClick={() => handleTestEvent('Camp Share', () => trackCampInteraction('share', 'test_camp_123', { method: 'email' }))}
              className="w-full"
              variant="outline"
            >
              Test Camp Share
            </Button>
            <Button 
              onClick={() => handleTestEvent('Camp Book', () => trackCampInteraction('book', 'test_camp_123', { price: 299 }))}
              className="w-full"
              variant="outline"
            >
              Test Camp Booking
            </Button>
          </CardContent>
        </Card>

        {/* Integration Events */}
        <Card>
          <CardHeader>
            <CardTitle>Integration Events</CardTitle>
            <CardDescription>Email and social features</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button 
              onClick={() => handleTestEvent('Email Connect', () => trackEmailIntegration('connect', { provider: 'gmail' }))}
              className="w-full"
              variant="outline"
            >
              Test Email Connect
            </Button>
            <Button 
              onClick={() => handleTestEvent('Email Scan', () => trackEmailIntegration('scan', { emails_found: 25 }))}
              className="w-full"
              variant="outline"
            >
              Test Email Scan
            </Button>
            <Button 
              onClick={() => handleTestEvent('Friend Request', () => trackSocialInteraction('send_request', { recipient: 'test_user' }))}
              className="w-full"
              variant="outline"
            >
              Test Friend Request
            </Button>
            <Button 
              onClick={() => handleTestEvent('Accept Request', () => trackSocialInteraction('accept_request', { sender: 'test_user' }))}
              className="w-full"
              variant="outline"
            >
              Test Accept Request
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Instructions */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>How to Verify Events</CardTitle>
          <CardDescription>Step-by-step testing guide</CardDescription>
        </CardHeader>
        <CardContent>
          <Alert className="mb-4 border-blue-200 bg-blue-50">
            <AlertDescription className="text-blue-800">
              <strong>💡 Pro Tip:</strong> Add <code className="px-1 py-0.5 bg-blue-100 rounded">?debug_mode=true</code> to the URL 
              to enable GA4 debug mode. Events will appear instantly in DebugView with detailed information.
              <br />
              <span className="text-sm mt-1 block">
                Example: <code className="px-1 py-0.5 bg-blue-100 rounded text-xs">https://your-url.com/analytics-test?debug_mode=true</code>
              </span>
            </AlertDescription>
          </Alert>

          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>Make sure you're in <strong>staging or production</strong> environment</li>
            <li><strong>(Optional)</strong> Add <code>?debug_mode=true</code> to URL for enhanced debugging</li>
            <li>Open <strong>Google Analytics</strong> in a separate tab</li>
            <li>Navigate to <strong>Configure → DebugView</strong></li>
            <li>Click any test button above</li>
            <li>Check DebugView - you should see the event appear within seconds</li>
            <li>Verify event name and parameters are correct</li>
            <li>If nothing appears, check:
              <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                <li>Environment variables are set correctly</li>
                <li>VITE_APP_ENV is 'staging' or 'production'</li>
                <li>VITE_GA_MEASUREMENT_ID is valid (G-XXXXXXXXXX)</li>
                <li>Browser console for errors</li>
                <li>Ad blockers are disabled</li>
              </ul>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* Documentation Link */}
      <div className="mt-6 text-center text-sm text-gray-600">
        <p>
          For more information, see{' '}
          <a href="/GOOGLE_ANALYTICS_SETUP.md" className="text-brand-600 hover:underline">
            GOOGLE_ANALYTICS_SETUP.md
          </a>
        </p>
      </div>
    </div>
  );
};

export default AnalyticsTest;

